import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const toRow = (s) => ({
  ...s,
  tick: s.suggested.length > 0 && s.suggested.every((g) => g.confident && g.options.length),
  picks: s.suggested.map((g) => ({ role: g.role, qty: g.qty, id: g.options[0]?.product_legacy_id || "", options: g.options })),
});

function SuggestRow({ row, onChange }) {
  const setPick = (i, k, v) => onChange({ ...row, picks: row.picks.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  return (
    <tr className="border-t border-slate-100 align-top" data-testid={`sub-suggest-row-${row.legacy_id}`}>
      <td className="py-2 pr-2"><input type="checkbox" className="accent-indigo-600 w-4 h-4" checked={row.tick} disabled={!row.picks.some((p) => p.id)} onChange={(e) => onChange({ ...row, tick: e.target.checked })} data-testid={`sub-suggest-tick-${row.legacy_id}`} /></td>
      <td className="py-2 pr-3 text-xs font-semibold text-slate-800 break-words"><span className="mr-1 rounded bg-slate-100 px-1 text-[10px] uppercase text-slate-600">{row.site === "old" ? "Old" : "New"}</span>{row.title}</td>
      <td className="py-2 space-y-1">
        {row.picks.length === 0 && <span className="text-xs text-amber-700">No match found — link by hand</span>}
        {row.picks.map((p, i) => (
          <div key={p.role} className="flex items-center gap-2">
            <select className="h-8 w-full min-w-0 flex-1 rounded border border-slate-200 px-1 text-xs" value={p.id} onChange={(e) => setPick(i, "id", e.target.value)} data-testid={`sub-suggest-pick-${row.legacy_id}-${p.role}`}>
              <option value="">— none —</option>
              {p.options.map((o) => <option key={o.product_legacy_id} value={o.product_legacy_id}>{o.title}</option>)}
            </select>
            <input type="number" min="1" className="h-8 w-14 rounded border border-slate-200 px-1 text-xs" value={p.qty} onChange={(e) => setPick(i, "qty", e.target.value)} data-testid={`sub-suggest-qty-${row.legacy_id}-${p.role}`} />
          </div>))}
      </td>
    </tr>
  );
}

export function SuggestFiltersDialog({ onClose }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/new-catalogue/subscriptions/suggestions").then((r) => setRows(r.data.items.map(toRow))).catch(() => setRows([])); }, []);
  const ticked = (rows || []).filter((r) => r.tick);
  const save = async () => {
    setBusy(true);
    let ok = 0;
    for (const r of ticked) {
      const filters = r.picks.filter((p) => p.id).map((p) => ({ product_legacy_id: p.id, qty: Number(p.qty) || 1 }));
      try { await api.put(`/new-catalogue/subscriptions/${r.legacy_id}/filters`, { confirm: true, filters }); ok += 1; }
      catch (e) { toast.error(`${r.title}: ${e.response?.data?.detail || "not saved"}`); }
    }
    setBusy(false);
    toast.success(`Filters linked on ${ok} subscription(s)`);
    onClose(true);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="sub-suggest-dialog">
        <DialogHeader><DialogTitle>Suggested filters</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Matched from each subscription's name. Confident matches are ticked; check the rest and the quantities per delivery before saving. Only TedOps is changed.</p>
        {!rows ? <p className="py-6 text-sm text-slate-500">Finding matches…</p> : (
          <table className="w-full table-fixed text-sm"><colgroup><col className="w-7" /><col className="w-[38%]" /><col /></colgroup><tbody>{rows.map((r, i) => <SuggestRow key={r.legacy_id} row={r} onChange={(n) => setRows((x) => x.map((y, j) => (j === i ? n : y)))} />)}</tbody></table>)}
        <div className="sticky bottom-0 flex items-center gap-3 bg-white pt-3">
          <Button disabled={busy || !ticked.length} onClick={save} data-testid="sub-suggest-save">{busy ? "Saving…" : `Link ticked (${ticked.length})`}</Button>
          <Button variant="ghost" onClick={() => onClose(false)} data-testid="sub-suggest-cancel">Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
